/* =========================================================================
   Mats · Home Hero  —  <mats-home-hero>
   - Carga el vídeo de desktop o móvil según el ancho (y lo cambia al rotar).
   - Pausar/reproducir + barra de progreso.
   - Pausa fuera de pantalla; respeta reduced-motion y ahorro de datos.
   - Mete el hero por debajo del header flotante midiendo el header real.
   Al ser un custom element, el editor de temas lo reinicia solo.
   ========================================================================= */
(() => {
  if (customElements.get('mats-home-hero')) return;

  const MOBILE = window.matchMedia('(max-width: 749px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SAVE_DATA = !!(navigator.connection && navigator.connection.saveData);

  const HEADER_SELECTORS = [
    '.shopify-section-group-header-group[id$="__header"]',
    '[id^="shopify-section-"][id$="__header"]',
    '.header-section',
    'header'
  ];

  class MatsHomeHero extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('.mats-home-hero__video');
      this.toggleBtn = this.querySelector('[data-hero-toggle]');
      this.toggleLabel = this.querySelector('[data-hero-toggle-label]');
      this.progress = this.querySelector('[data-hero-progress]');
      this.userPaused = REDUCED.matches || SAVE_DATA;
      this.inView = true;
      this.pull = 0;

      this._onMQ = () => this.loadSource();
      this._onResize = () => {
        cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = requestAnimationFrame(() => this.measure());
      };
      this._onToggle = () => this.toggle();
      this._onScrollLink = (e) => this.scrollNext(e);
      this._tick = () => this.tick();

      this.quick = this.querySelector('.mats-home-hero__quick');

      this.measure();
      window.addEventListener('resize', this._onResize, { passive: true });
      window.addEventListener('load', this._onResize, { once: true });

      const header = this.findHeader();
      if (header && 'ResizeObserver' in window) {
        this._ro = new ResizeObserver(this._onResize);
        this._ro.observe(header);
      }

      this.querySelectorAll('[data-hero-scroll]').forEach((a) =>
        a.addEventListener('click', this._onScrollLink)
      );

      if (!this.video) return;

      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.addEventListener('playing', () => {
        this.classList.add('is-playing');
        this.setPausedUI(false);
        this.startTick();
      });
      this.video.addEventListener('pause', () => this.setPausedUI(true));
      this.video.addEventListener('loadeddata', () => this.classList.add('has-frame'));

      if (this.toggleBtn) this.toggleBtn.addEventListener('click', this._onToggle);
      MOBILE.addEventListener('change', this._onMQ);

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          if (this.inView) this.play();
          else this.video.pause();
        }, { threshold: 0.05 });
        this._io.observe(this);
      }

      this.setPausedUI(this.userPaused);
      this.loadSource();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      if (this._onScrollTop) window.removeEventListener('scroll', this._onScrollTop);
      MOBILE.removeEventListener('change', this._onMQ);
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      cancelAnimationFrame(this._raf);
      cancelAnimationFrame(this._resizeRaf);
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
      }
    }

    /* ---------- Vídeo ---------- */
    loadSource() {
      const { srcDesktop, srcMobile } = this.dataset;
      const src = MOBILE.matches ? (srcMobile || srcDesktop) : (srcDesktop || srcMobile);
      if (!src || this.video.dataset.current === src) return;

      this.video.dataset.current = src;
      this.classList.remove('is-playing', 'has-frame');
      this.video.src = src;
      this.video.load();
      this.play();
    }

    play() {
      if (!this.video || this.userPaused || !this.inView) return;
      const p = this.video.play();
      if (p && p.catch) {
        p.catch(() => {
          // Autoplay bloqueado (modo ahorro de batería en iOS, etc.)
          this.setPausedUI(true);
        });
      }
    }

    toggle() {
      if (!this.video) return;
      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    setPausedUI(paused) {
      this.classList.toggle('is-paused', paused);
      if (!this.toggleBtn) return;
      const label = paused ? this.dataset.playLabel : this.dataset.pauseLabel;
      if (this.toggleLabel && label) this.toggleLabel.textContent = label;
      this.toggleBtn.setAttribute('aria-pressed', String(paused));
    }

    startTick() {
      if (!this.progress) return;
      cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(this._tick);
    }

    tick() {
      const v = this.video;
      if (v && v.duration) {
        this.progress.style.transform = `scaleX(${Math.min(v.currentTime / v.duration, 1)})`;
      }
      if (v && !v.paused) this._raf = requestAnimationFrame(this._tick);
    }

    /* ---------- Header flotante ---------- */
    findHeader() {
      for (const sel of HEADER_SELECTORS) {
        const el = document.querySelector(sel);
        if (el && el !== this && !this.contains(el)) return el;
      }
      return null;
    }

    isFirstSection() {
      const section = this.closest('.shopify-section');
      if (!section) return false;
      let prev = section.previousElementSibling;
      while (prev && (prev.offsetHeight === 0 || prev.matches('script, style, link'))) {
        prev = prev.previousElementSibling;
      }
      return !prev;
    }

    measure() {
      // Alto REAL de la barra de accesos: el escenario mide 100svh menos esto.
      if (this.quick) this.style.setProperty('--mhh-quick-h', `${this.quick.offsetHeight}px`);

      const header = this.findHeader();
      if (!header) {
        this.setOffset();
        return;
      }

      // Con la página desplazada (scroll restaurado al recargar o volver atrás,
      // barra de URL del móvil, etc.) el header sticky da medidas falsas: un
      // --mhh-header inflado hacía crecer el escenario y el vídeo/imagen se veía
      // con zoom. Solo medimos arriba del todo; si no, esperamos a llegar arriba.
      if (window.scrollY > 4) {
        this.waitForTop();
        return;
      }

      const underHeader =
        this.classList.contains('mats-home-hero--under-header') && this.isFirstSection();

      let pull = 0;
      if (underHeader) {
        const pos = getComputedStyle(header).position;
        if (pos !== 'fixed' && pos !== 'absolute') {
          const heroTop = this.getBoundingClientRect().top + this.pull; // posición sin el ajuste
          pull = Math.max(0, Math.round(heroTop - header.getBoundingClientRect().top));
        }
      }
      this.pull = pull;
      this.style.setProperty('--mhh-pull', `${pull}px`);
      this.setOffset();

      // Borde inferior VISIBLE de la barra de cristal (no la caja de la sección,
      // que puede no incluir el margen flotante), relativo al inicio del hero.
      const bar = this.visibleHeaderBar(header);
      const offset = Math.round(bar.getBoundingClientRect().bottom - this.getBoundingClientRect().top);
      if (offset > 0) this.style.setProperty('--mhh-header', `${offset}px`);
    }

    waitForTop() {
      if (this._onScrollTop) return;
      this._onScrollTop = () => {
        if (window.scrollY > 4) return;
        window.removeEventListener('scroll', this._onScrollTop);
        this._onScrollTop = null;
        this.measure();
      };
      window.addEventListener('scroll', this._onScrollTop, { passive: true });
    }

    /* Lo que queda por encima del hero (barra superior mats-topbar, anuncios…)
       se descuenta de la altura: así todo junto + accesos caben en la 1.ª pantalla. */
    setOffset() {
      const top = this.isFirstSection()
        ? Math.max(0, Math.round(this.getBoundingClientRect().top + window.scrollY))
        : 0;
      this.style.setProperty('--mhh-offset', `${top}px`);
    }

    visibleHeaderBar(header) {
      // El elemento con fondo/borde más bajo dentro del header = la barra de cristal.
      let best = header;
      let bestBottom = header.getBoundingClientRect().bottom;
      const candidates = header.querySelectorAll('header, .header, [class*="header__wrapper"], [class*="header-glass"], [class*="header__inner"]');
      candidates.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.height < 30 || r.height > 220) return;
        if (r.bottom > bestBottom || best === header) {
          best = el;
          bestBottom = r.bottom;
        }
      });
      return best;
    }

    /* ---------- «Novedades ↓» ---------- */
    scrollNext(e) {
      const section = this.closest('.shopify-section');
      const next = section && section.nextElementSibling;
      if (!next) return;
      e.preventDefault();
      const top = next.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top, behavior: REDUCED.matches ? 'auto' : 'smooth' });
    }
  }

  customElements.define('mats-home-hero', MatsHomeHero);
})();
